import { useState } from "react";
import { Box, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useApiVersion } from "@/hooks/useApiVersion";
import { useUxBuildInfo } from "@/hooks/useUxBuildInfo";
import type { UxBuildInfo } from "@/services/uxBuildInfo";
import { USE_AUTH } from "@/config/auth";

const uxVersion = __APP_VERSION__;

const formatBuildTime = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const buildClipboardText = (
  api: { version: string; imageTag: string; buildTime: string; environment: string } | null,
  ux: UxBuildInfo | null
): string => {
  const title = import.meta.env.VITE_APP_TITLE || "Coronis Management";
  const lines = [title, "---"];

  lines.push(`UX Version: ${uxVersion}`);

  if (ux) {
    lines.push(`UX Build: ${ux.shortSha} (${ux.branch})`);
    lines.push(`UX Built: ${formatBuildTime(ux.builtAt)}`);
    if (ux.pipelineBuildNumber) lines.push(`UX Pipeline: ${ux.pipelineBuildNumber}`);
    if (ux.pipelineBuildId) lines.push(`UX Pipeline Build ID: ${ux.pipelineBuildId}`);
  }

  if (api) {
    lines.push(`API Version: ${api.version}`);
    lines.push(`Image Tag: ${api.imageTag}`);
    lines.push(`Build Time: ${formatBuildTime(api.buildTime)}`);
    lines.push(`Environment: ${api.environment}`);
  } else {
    lines.push("API Version: unavailable");
  }

  lines.push("---");
  lines.push(`Base URI: ${import.meta.env.VITE_APP_BASE_URI || "—"}`);
  lines.push(`API URL: ${import.meta.env.VITE_API_SERVER_URL || "—"}`);
  lines.push(`MSAL Client ID: ${import.meta.env.VITE_MSAL_CLIENT_ID || "—"}`);
  lines.push(`MSAL Authority: ${import.meta.env.VITE_MSAL_AUTHORITY || "—"}`);
  lines.push(`OIDC Scopes: ${import.meta.env.VITE_OIDC_OPTIONS_DEFAULT_SCOPES || "—"}`);
  lines.push(`Auth Enabled: ${USE_AUTH}`);
  lines.push("---");
  lines.push(`URL: ${window.location.href}`);

  return lines.join("\n");
};

interface DetailRowProps {
  label: string;
  value: string;
}

const DetailRow = ({ label, value }: DetailRowProps) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 3 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="caption" fontFamily="monospace" sx={{ fontWeight: 500 }}>
      {value}
    </Typography>
  </Box>
);

export const ApiVersionIndicator = () => {
  const { data, isLoading, error } = useApiVersion();
  const ux = useUxBuildInfo();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [copied, setCopied] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCopied(false);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(buildClipboardText(data, ux));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const open = Boolean(anchorEl);

  const displayVersion = data?.version ? `v${data.version}` : isLoading ? "..." : `v${uxVersion}`;

  return (
    <>
      <Tooltip title="Version info">
        <Typography
          variant="caption"
          onClick={handleClick}
          sx={{
            cursor: "pointer",
            color: "text.disabled",
            fontFamily: "monospace",
            fontSize: 11,
            "&:hover": { color: "text.secondary" },
            userSelect: "none",
          }}
        >
          {displayVersion}
        </Typography>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { p: 2, minWidth: 280 } } }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle2">Version Details</Typography>
          <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
            <IconButton size="small" onClick={handleCopy}>
              {copied ? <CheckIcon fontSize="small" color="success" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <DetailRow label="UX Version" value={uxVersion} />
          {ux && (
            <>
              <DetailRow label="UX Build" value={`${ux.shortSha} (${ux.branch})`} />
              <DetailRow label="UX Built" value={formatBuildTime(ux.builtAt)} />
            </>
          )}

          {error || (!isLoading && !data) ? (
            <DetailRow label="API Version" value="unavailable" />
          ) : isLoading ? (
            <DetailRow label="API Version" value="loading..." />
          ) : (
            data && (
              <>
                <DetailRow label="API Version" value={data.version} />
                <DetailRow label="Image Tag" value={data.imageTag} />
                <DetailRow label="Build Time" value={formatBuildTime(data.buildTime)} />
                <DetailRow label="Environment" value={data.environment} />
              </>
            )
          )}
        </Box>
      </Popover>
    </>
  );
};
